import { action, makeObservable, observable } from 'mobx';
import { z } from 'zod';
import { UnreachableError } from './helpers';
import { getLocalStorageItem, listenToStorageEvent } from './localstorage';
import { convertRelumeCFVersion } from './webflow/convert_relume_versions';
import { convertComponentToSpacingBlocks } from './webflow/convert_spacing';
import { convertToFinsweet } from './webflow/convert_to_finsweet';
import { removeBlocksDataAttributes } from './webflow/data_attributes';
import { Component } from './webflow/schema';
import { zodParse } from './zod_parse';

export enum WebflowStyleGuide {
  RelumeV3_CF2_1 = 'relume',
  RelumeV2_CF2_1 = 'relumev21',
  RelumeV1_5_CF2 = 'relumev2',
  Finsweet_CF2_1 = 'finsweet',
  Finsweet_CF2 = 'finsweetv2',
}

/** Stores the settings that apply when copying components */
export interface CopySettingsStore {
  readonly value: Readonly<CopySettings>;
  set(newValue: Omit<CopySettings, 'updatedAt'>): void;
}

const STORAGE_KEY = 'relumeCopySettings';

const schema = z.object({
  style: z.nativeEnum(WebflowStyleGuide),
  spacing: z.enum(['wrappers', 'blocks']),
  updatedAt: z.number(),
});
export type CopySettings = z.infer<typeof schema>;
export const DEFAULT_VALUE: CopySettings = {
  style: WebflowStyleGuide.RelumeV3_CF2_1,
  spacing: 'wrappers',
  updatedAt: 0,
};

function sameSettings(
  v1: Omit<CopySettings, 'updatedAt'>,
  v2: Omit<CopySettings, 'updatedAt'>,
) {
  return v1.style === v2.style && v1.spacing === v2.spacing;
}

export function parseOrDefault(v: unknown) {
  if (typeof v !== 'string' || v.trim().length === 0) {
    // don't display errors for empty values
    return DEFAULT_VALUE;
  }
  try {
    return zodParse(schema, JSON.parse(v));
  } catch (e) {
    console.log('Error parsing copy settings', e);
    return DEFAULT_VALUE;
  }
}

/** Stored in the chrome extension storage */
export class ChromeCopySettingsStore implements CopySettingsStore {
  static async create() {
    const batch = await chrome.storage.sync.get([STORAGE_KEY]);
    const v: unknown = batch[STORAGE_KEY];
    return new ChromeCopySettingsStore(parseOrDefault(v));
  }

  private constructor(public value: CopySettings) {
    makeObservable(this, {
      value: observable.ref,
      set: action.bound,
    });
    chrome.storage.sync.onChanged.addListener((changes) => {
      if (STORAGE_KEY in changes) {
        this.value = parseOrDefault(changes[STORAGE_KEY].newValue);
      }
    });
  }

  set(newValue: Omit<CopySettings, 'updatedAt'>) {
    if (sameSettings(newValue, this.value)) return;
    const v = { ...newValue, updatedAt: +new Date() };
    chrome.storage.sync.set({ [STORAGE_KEY]: JSON.stringify(v) });
    this.value = v;
  }
}

function tryCreateBroadcastChannel() {
  try {
    return new BroadcastChannel('LocalStorageCopySettingsStore');
  } catch (e) {
    // Figma runs in an opaque origin, so BroadcastChannel fails to create
    console.log('Error creating broadcast channel', e);
    return undefined;
  }
}

const localUpdateChange = tryCreateBroadcastChannel();

/** Stored in local stroage.  This should only be used on www.relume.io */
class LocalStorageCopySettingsStore implements CopySettingsStore {
  public value!: CopySettings;

  constructor() {
    this.update();
    makeObservable(this, {
      value: observable.ref,
      set: action.bound,
    });
    listenToStorageEvent(() => this.update());
    localUpdateChange?.addEventListener('message', () => this.update());
  }

  private update() {
    this.value = parseOrDefault(getLocalStorageItem(STORAGE_KEY) || '');
  }

  set(newValue: Omit<CopySettings, 'updatedAt'>) {
    if (sameSettings(newValue, this.value)) return;
    const v = { ...newValue, updatedAt: +new Date() };
    localStorage[STORAGE_KEY] = JSON.stringify(v);
    this.value = v;
    localUpdateChange?.postMessage(null);
  }
}

export const localStorageCopySettings = new LocalStorageCopySettingsStore();

export function applyCopySettings(
  settings: Omit<CopySettings, 'updatedAt'>,
  component: Component,
): Component {
  const { style, spacing } = settings;
  component = removeBlocksDataAttributes(component);
  // remove this conversion step after we release export for SGB and update the style guide in webflow
  switch (style) {
    case WebflowStyleGuide.RelumeV3_CF2_1:
      component = convertRelumeCFVersion(component, '3');
      break;
    case WebflowStyleGuide.RelumeV2_CF2_1:
    case WebflowStyleGuide.Finsweet_CF2_1:
      component = convertRelumeCFVersion(component, '2.1');
      break;
    case WebflowStyleGuide.Finsweet_CF2:
    case WebflowStyleGuide.RelumeV1_5_CF2:
      component = convertToFinsweet(component, '2');
      break;
    default:
      throw new UnreachableError(style);
  }
  if (spacing === 'blocks') {
    component = convertComponentToSpacingBlocks(component);
  }
  return component;
}
